/**
 * Mock Conversations Client (cloud chat conversations).
 * Implements IConversationsClient with LocalStorage persistence,
 * mirroring the live `/api/conversations` semantics (auto-title, ordering).
 */

import {
  CONVERSATION_UNTITLED,
  conversationSummaryOf,
  deriveConversationTitle,
  isValidConversationRole,
  MAX_CONVERSATIONS,
  MAX_MESSAGE_TEXT,
  type AddConversationMessageInput,
  type Conversation,
  type ConversationSummary,
  type CreateConversationInput,
} from '@zunelo/shared';
import { StorageService } from '../storageService';
import { ApiEnvelope } from '../../domain/types';
import type { ClientConfig, IConversationsClient } from './types';
import { simulateNetwork, DEFAULT_CONFIG } from './mockClientUtils';

export const MOCK_CONVERSATIONS_KEY = 'zunelo_mock_conversations';

function newId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function resetMockConversations(): void {
  StorageService.removeItem(MOCK_CONVERSATIONS_KEY);
}

function loadConversations(): Conversation[] {
  const stored = StorageService.get<Conversation[]>(MOCK_CONVERSATIONS_KEY, []);
  return Array.isArray(stored) ? stored : [];
}

function saveConversations(conversations: Conversation[]): void {
  StorageService.set(MOCK_CONVERSATIONS_KEY, conversations);
}

export class MockConversationsClient implements IConversationsClient {
  private config: ClientConfig;

  constructor(config: ClientConfig = DEFAULT_CONFIG) {
    this.config = config;
  }

  private create(nowIso: string, input: CreateConversationInput): Conversation {
    const seedMessages = Array.isArray(input.seedMessages)
      ? input.seedMessages
          .filter(
            (m) =>
              isValidConversationRole(m.role) &&
              typeof m.text === 'string' &&
              m.text.trim().length > 0,
          )
          .slice(-50)
      : [];
    const anchor = input.anchor;
    return {
      id: newId('conv'),
      title: anchor?.title?.trim() || CONVERSATION_UNTITLED,
      createdAt: nowIso,
      updatedAt: nowIso,
      ...(anchor ? { anchor } : {}),
      messages: seedMessages,
    };
  }

  async getConversations(): Promise<ApiEnvelope<ConversationSummary[]>> {
    return simulateNetwork(() => {
      return loadConversations()
        .slice(0, MAX_CONVERSATIONS)
        .map(conversationSummaryOf)
        .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0));
    }, this.config);
  }

  async createConversation(input: CreateConversationInput): Promise<ApiEnvelope<Conversation>> {
    return simulateNetwork(() => {
      const nowIso = new Date().toISOString();
      const created = this.create(nowIso, input);
      saveConversations([created, ...loadConversations()]);
      return created;
    }, this.config);
  }

  async getConversation(id: string): Promise<ApiEnvelope<Conversation>> {
    return simulateNetwork(() => {
      const found = loadConversations().find((c) => c.id === id);
      if (!found) {
        const error = new Error(`Conversation ${id} not found.`) as Error & { status?: number; code?: string };
        error.status = 404;
        error.code = 'NOT_FOUND';
        throw error;
      }
      return found;
    }, this.config);
  }

  async addMessage(id: string, message: AddConversationMessageInput): Promise<ApiEnvelope<Conversation>> {
    return simulateNetwork(() => {
      if (!isValidConversationRole(message.role)) {
        const error = new Error('Message role is invalid.') as Error & { status?: number; code?: string };
        error.status = 400;
        error.code = 'BAD_REQUEST';
        throw error;
      }
      const text = (message.text ?? '').trim();
      if (!text || text.length > MAX_MESSAGE_TEXT) {
        const error = new Error('Message text must be 1-4000 characters.') as Error & { status?: number; code?: string };
        error.status = 400;
        error.code = 'BAD_REQUEST';
        throw error;
      }
      const conversations = loadConversations();
      const idx = conversations.findIndex((c) => c.id === id);
      if (idx < 0) {
        const error = new Error(`Conversation ${id} not found.`) as Error & { status?: number; code?: string };
        error.status = 404;
        error.code = 'NOT_FOUND';
        throw error;
      }
      const nowIso = new Date().toISOString();
      const current = conversations[idx];
      const isUntitled = current.title === CONVERSATION_UNTITLED;
      const title =
        isUntitled && message.role === 'user'
          ? deriveConversationTitle(text) || CONVERSATION_UNTITLED
          : current.title;
      const next: Conversation = {
        ...current,
        title,
        messages: [
          ...current.messages,
          {
            id: newId('m'),
            role: message.role,
            text,
            createdAt: nowIso,
          },
        ].slice(-200),
        updatedAt: nowIso,
      };
      conversations[idx] = next;
      saveConversations(conversations);
      return next;
    }, this.config);
  }

  async deleteConversation(id: string): Promise<ApiEnvelope<{ deleted: boolean }>> {
    return simulateNetwork(() => {
      saveConversations(loadConversations().filter((c) => c.id !== id));
      return { deleted: true };
    }, this.config);
  }
}